// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

// Utils
import {
    getCardDimensionPropertiesPx,
    getEditorOverflowDistance,
    getIsAutoHeight,
    getIsEditorOverThreshold,
} from './resizableEditorUtils';
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import { sendAmplitudeEvent } from '../../../analytics/amplitudeService';
import { getElementSyncedMeasurements } from '../../../components/measurementsStore/elementMeasurements/syncedMeasurementsSingleton';

// Actions
import { navigateToElement } from '../../../reducers/navigationActions';
import { getNextUndoElementUpdateTransactionId } from '../../../utils/undoRedo/undoRedoActions';

// Constants
import { ELEMENT_DEFAULT_WIDTH } from '../../../../common/elements/elementConstants';
import { AMPLITUDE_USER_PROPS, TRACKED_FEATURES } from '../../../../common/analytics/statsConstants';
import { EVENT_TYPE_NAMES } from '../../../../common/analytics/amplitudeEventTypesUtil';
import { BORDER_OFFSET_PX } from '../../../utils/grid/gridUtils';

export const CARD_MIN_WIDTH = ELEMENT_DEFAULT_WIDTH / 2;
const SNAP_THRESHOLD = 2;

const snapSize = (size) => {
    const { width } = size;

    const defaultWidthDiff = Math.abs(width - ELEMENT_DEFAULT_WIDTH);

    return {
        ...size,
        width: defaultWidthDiff < SNAP_THRESHOLD ? ELEMENT_DEFAULT_WIDTH : width,
    };
};

const mapDispatchToProps = (dispatch) => ({
    dispatchNavigateToElement: ({ elementId }) => dispatch(navigateToElement({ elementId })),
    dispatchGetNextUndoTransactionId: () => dispatch(getNextUndoElementUpdateTransactionId()),
});

// Determined via experimentation, not math
const AUTO_HEIGHT_MIN = 0;
const AUTO_HEIGHT_MAX = 20;

const getChangesForOverflowAmount = (overflowAmount, currentContainerHeight, isMultiResizingOrAnimating) => {
    if (!isMultiResizingOrAnimating && overflowAmount <= AUTO_HEIGHT_MAX && overflowAmount >= AUTO_HEIGHT_MIN) {
        // container roughly matches the text - set auto height mode
        return {
            minHeight: null,
            maxHeight: null,
            autoHeight: true,
        };
    }

    if (overflowAmount > 0) {
        // text is overflowing (as the user intends) - set max height to
        // stop the container growing to fit it.
        return {
            minHeight: null,
            maxHeight: currentContainerHeight,
            autoHeight: false,
        };
    }

    // text has extra space - set min height so it doesn't collapse
    return {
        minHeight: currentContainerHeight,
        maxHeight: null,
        autoHeight: false,
    };
};

export default ({ getMinContainerHeight, containerPaddingGu = 0 }) =>
    (DecoratedComponent) => {
        @connect(null, mapDispatchToProps)
        class CardSaveHeightResizeDecorator extends React.Component {
            constructor(props) {
                super(props);

                this.initialised = false;
                this.initialElementHeight = null;
                this.initialContainerHeight = null;

                this.editorHeight = null;

                this.containerElement = null;
                this.editorElement = null;

                this.state = {
                    tempContainerHeight: null,
                };
            }

            containerRef = (node) => {
                this.containerElement = node;
            };

            editorRef = (node) => {
                this.editorElement = node;
            };

            getCurrentContainerHeight = () => {
                const { getContextZoomScale } = this.props;

                const zoomScale = getContextZoomScale();

                return this.containerElement.getBoundingClientRect().height / zoomScale;
            };

            getEditorHeight = () => {
                const { getContextZoomScale } = this.props;
                const zoomScale = getContextZoomScale();

                // TODO-REMOVE-DRAFT: On removal restructure this component's logic to not require subtracting padding
                const isTiptap = this.editorElement?.classList?.contains('tiptap') ?? false;
                const padding = isTiptap ? containerPaddingGu * this.props.gridSize : 0;

                return this.editorElement.getBoundingClientRect().height / zoomScale - padding;
            };

            initialiseComponentHeights = () => {
                const { elementId } = this.props;

                this.initialised = true;

                // Use synced measurements singleton to always have the latest measurements
                this.initialElementHeight = getElementSyncedMeasurements(elementId)?.height || 0;
                this.initialContainerHeight = this.getCurrentContainerHeight();
            };

            getMinimumContainerHeight = (currentEditorHeight) => {
                return getMinContainerHeight(this.props, currentEditorHeight);
            };

            getNewContainerHeight = ({ height }, currentEditorHeight) => {
                const { gridSize } = this.props;

                const minimumContainerHeight = this.getMinimumContainerHeight(currentEditorHeight);

                // Container Height = Element Height - Constant
                // Constant = Initial Element Height - Initial Container Height
                // Therefore:
                // New Container Height     = Desired Element Height - (Initial Element Height - Initial Container Height)
                //                          = Desired Element Height - Initial Element Height + Initial Container Height
                const desiredElementHeightPx = height * gridSize;

                return Math.max(
                    desiredElementHeightPx - this.initialElementHeight + this.initialContainerHeight,
                    minimumContainerHeight,
                );
            };

            resetState = () => {
                const { setTempElementSize } = this.props;

                setTempElementSize(null);
                this.setState({ tempContainerHeight: null });
                this.initialised = false;
            };

            setContainerSizeWhileResizing = (size) => {
                const { setTempElementSize, isMultiResizingOrAnimating } = this.props;

                if (!size) {
                    this.resetState();
                    return;
                }

                if (!this.initialised) this.initialiseComponentHeights();

                const newHeight = this.getNewContainerHeight(size, this.editorHeight);
                this.setState({ tempContainerHeight: newHeight });

                setTempElementSize(isMultiResizingOrAnimating ? size : snapSize(size));
            };

            setElementSizeAndPosition = (size, position) => {
                const {
                    setElementSizeAndPosition,
                    dispatchStopEditing,
                    isMultiResizingOrAnimating,
                    element,
                    gridSize,
                    isEditing,
                    inList,
                } = this.props;

                if (!size && !position) return;

                if (!size) {
                    setElementSizeAndPosition(null, position);
                    return;
                }

                if (!this.initialised) this.initialiseComponentHeights();

                const newSize = isMultiResizingOrAnimating ? size : snapSize(size);

                newSize.height = Math.round(size.height) - BORDER_OFFSET_PX / gridSize;

                this.editorHeight = this.getEditorHeight();

                this.containerHeight = this.getNewContainerHeight(newSize, this.editorHeight);
                const containerHeightGridUnits = this.containerHeight / gridSize;

                // Use the newly saved max height as the max height to compare against
                const overflowDistance = getEditorOverflowDistance(
                    this.containerHeight,
                    this.editorHeight,
                    containerPaddingGu,
                    gridSize,
                );

                const changes = getChangesForOverflowAmount(
                    overflowDistance,
                    containerHeightGridUnits,
                    isMultiResizingOrAnimating,
                );

                // If the element is in a list, don't update its width
                if (!inList) {
                    changes.width = Math.max(Math.round(newSize.width), CARD_MIN_WIDTH);
                }

                const elementId = getElementId(element);

                // Stop editing if resizing into a height limited size
                if (changes.maxHeight && isEditing) dispatchStopEditing(elementId);

                setElementSizeAndPosition(changes, position);

                sendAmplitudeEvent({
                    eventType: EVENT_TYPE_NAMES.ELEMENT_UPDATE,
                    userProperties: {
                        [AMPLITUDE_USER_PROPS.FEATURE]: { [TRACKED_FEATURES.RESIZE_CARD]: true },
                    },
                });
            };

            handleDoubleClick = () => {
                const { element, dispatchUpdateElement, gridSize } = this.props;

                this.editorHeight = this.getEditorHeight();

                const { minHeight, maxHeight } = getCardDimensionPropertiesPx({ element, gridSize });
                const autoHeight = getIsAutoHeight(element);

                const canToggle =
                    // If the editor height is less than the min height, then we may want to toggle the state of auto height
                    (minHeight && this.editorHeight < minHeight) ||
                    // If the editor height is greater than the max height, then we may want to toggle the auto height
                    (maxHeight &&
                        getIsEditorOverThreshold(maxHeight, this.editorHeight, this.editorElement.offsetTop, gridSize));

                if (!canToggle) return;

                return dispatchUpdateElement({
                    id: getElementId(element),
                    changes: {
                        autoHeight: !autoHeight,
                    },
                });
            };

            render() {
                const { tempContainerHeight } = this.state;

                return (
                    <DecoratedComponent
                        {...this.props}
                        cardEditorRef={this.editorRef}
                        editorRef={this.editorRef}
                        containerRef={this.containerRef}
                        tempContainerHeight={tempContainerHeight}
                        handleDoubleClick={this.handleDoubleClick}
                        setElementSizeAndPosition={this.setElementSizeAndPosition}
                        setTempElementSize={this.setContainerSizeWhileResizing}
                    />
                );
            }
        }

        CardSaveHeightResizeDecorator.propTypes = {
            element: PropTypes.object.isRequired,
            setElementSizeAndPosition: PropTypes.func,
            setTempElementSize: PropTypes.func,
            dispatchUpdateElement: PropTypes.func,
            dispatchNavigateToElement: PropTypes.func,
            dispatchStopEditing: PropTypes.func,
            gridSize: PropTypes.number,
            getContextZoomScale: PropTypes.func,
            isEditing: PropTypes.bool,
            inList: PropTypes.string,
            isMultiResizingOrAnimating: PropTypes.bool,
            dispatchGetNextUndoTransactionId: PropTypes.func,
        };

        return CardSaveHeightResizeDecorator;
    };
